import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import type { UtilityListItem } from '../types/utility';
import { rankUtilities, utilityOptionLabel } from '../lib/utilityPicker';

/**
 * The utility picker.
 *
 * A national table of ~3,400 rows cannot be a list you scroll, so this shows
 * the utilities that actually serve the customer's ZIP — a median of 2 — and
 * searches the whole table the moment anyone types. utilities.md's rule that a
 * user must never fail to see their utility is met by search reaching every
 * row; the short list is what makes the common case one glance instead of a
 * scroll.
 */

export const OTHER_VALUE = 'other';
const MIN_SEARCH = 2;
const DEBOUNCE_MS = 200;

interface UtilityComboboxProps {
  /** '' when unset, OTHER_VALUE for "not listed", otherwise the utility id. */
  value: string;
  /** Name to show for the current selection; the row may not be in any list. */
  selectedName: string | null;
  /** The likely answers, already fetched: ZIP matches, or the state's list. */
  likely: UtilityListItem[];
  /** Heading over the likely list, e.g. "Serves ZIP 05401". */
  likelyLabel: string;
  projectState: string | null;
  loading?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  onSelect: (value: string, utility?: UtilityListItem) => void;
  /** Searches the whole table. Rejections surface as a message, never a throw. */
  onSearch: (term: string) => Promise<UtilityListItem[]>;
}

export function UtilityCombobox({
  value,
  selectedName,
  likely,
  likelyLabel,
  projectState,
  loading = false,
  disabled = false,
  invalid = false,
  onSelect,
  onSearch,
}: UtilityComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UtilityListItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const searching_ = query.trim().length >= MIN_SEARCH;

  // Debounced search. The counter guards against an earlier, slower response
  // overwriting a later one — the classic out-of-order autocomplete bug.
  const runId = useRef(0);
  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_SEARCH) {
      setResults(null);
      setSearching(false);
      setSearchError(false);
      return;
    }
    const id = ++runId.current;
    setSearching(true);
    setSearchError(false);
    const t = setTimeout(() => {
      onSearch(term)
        .then((rows) => {
          if (id !== runId.current) return;
          setResults(rows);
          setSearching(false);
        })
        .catch(() => {
          if (id !== runId.current) return;
          setResults([]);
          setSearching(false);
          setSearchError(true);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, onSearch]);

  const options = useMemo(() => {
    const rows = searching_ ? results ?? [] : likely;
    return rankUtilities(rows, searching_ ? query : '', projectState);
  }, [searching_, results, likely, query, projectState]);

  useEffect(() => setActive(0), [options.length, open]);

  // Close on an outside click. Focus alone is not enough: a click on the list
  // moves focus out of the input before the option's own handler runs.
  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long result list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const total = options.length + 1; // + the "not listed" row, always last

  function choose(index: number) {
    if (index >= options.length) {
      onSelect(OTHER_VALUE);
    } else {
      const u = options[index];
      if (!u) return;
      onSelect(u.id, u);
    }
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % total : (i - 1 + total) % total));
      return;
    }
    if (e.key === 'Enter' && open) { e.preventDefault(); choose(active); return; }
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); setQuery(''); return; }
    if (e.key === 'Tab') setOpen(false);
  }

  const display = value === OTHER_VALUE ? 'Other (not listed)' : selectedName ?? '';
  const border = invalid ? 'border-red-500 focus-within:ring-red-500' : 'border-gray-300 focus-within:ring-blue-500';

  return (
    <div ref={rootRef} className="relative">
      <div
        className={`flex items-center gap-2 w-full px-3 py-2 border rounded-md bg-white focus-within:outline-none focus-within:ring-2 ${border} ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      >
        <Search size={16} className="text-gray-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="utility-listbox"
          aria-autocomplete="list"
          aria-activedescendant={open ? `utility-option-${active}` : undefined}
          value={open ? query : display}
          placeholder={value ? '' : 'Search or select a utility'}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => !disabled && setOpen(true)}
          onClick={() => !disabled && setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          data-testid="utility-id"
          className="flex-1 min-w-0 outline-none bg-transparent disabled:cursor-not-allowed"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={() => { onSelect(''); setQuery(''); setOpen(false); }}
            data-testid="utility-clear"
            aria-label="Clear the selected utility"
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X size={15} />
          </button>
        )}
        <ChevronDown
          size={16}
          className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {open && (
        <ul
          ref={listRef}
          id="utility-listbox"
          role="listbox"
          data-testid="utility-listbox"
          className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg py-1"
        >
          <li className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
            {searching_
              ? searching
                ? 'Searching every utility…'
                : `${options.length} match${options.length === 1 ? '' : 'es'} across all states`
              : likelyLabel}
          </li>

          {!searching_ && likely.length === 0 && !loading && (
            <li className="px-3 py-2 text-sm text-gray-500" data-testid="utility-empty-likely">
              Start typing to search every utility in the country.
            </li>
          )}
          {loading && !searching_ && (
            <li className="px-3 py-2 text-sm text-gray-500">Loading…</li>
          )}
          {searchError && (
            <li className="px-3 py-2 text-sm text-red-600" data-testid="utility-search-error">
              Search is unavailable right now. Choose "Other (not listed)" and type the name.
            </li>
          )}
          {searching_ && !searching && options.length === 0 && !searchError && (
            <li className="px-3 py-2 text-sm text-gray-500" data-testid="utility-no-results">
              No utility matches "{query.trim()}".
            </li>
          )}

          {options.map((u, i) => (
            <li
              key={u.id}
              id={`utility-option-${i}`}
              data-index={i}
              data-testid={`utility-option-${u.id}`}
              role="option"
              aria-selected={value === u.id}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(i)}
              className={`px-3 py-2 text-sm cursor-pointer ${
                i === active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
              }`}
            >
              {utilityOptionLabel(u, projectState)}
            </li>
          ))}

          <li
            id={`utility-option-${options.length}`}
            data-index={options.length}
            data-testid="utility-option-other"
            role="option"
            aria-selected={value === OTHER_VALUE}
            onMouseEnter={() => setActive(options.length)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => choose(options.length)}
            className={`px-3 py-2 text-sm cursor-pointer border-t border-gray-100 ${
              options.length === active ? 'bg-blue-50 text-blue-900' : 'text-gray-700'
            }`}
          >
            Other (not listed)
          </li>
        </ul>
      )}
    </div>
  );
}
